import React, {
    Dispatch as DispatchFromReact,
    EffectCallback,
    Fragment,
    ReactElement,
    SetStateAction,
    useEffect,
    useMemo,
    useState
} from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// Components

import AgreeToFileAttestation from './pageIncludes/signAndReviewAttestations/AgreeToFileAttestation';
import ClaimNoticeAttestation from './pageIncludes/signAndReviewAttestations/ClaimNoticeAttestation';
import CoverageEndAttestation from './pageIncludes/signAndReviewAttestations/CoverageEndAttestation';
import Form from '../../../lib/wrappers/Form';
import IncarcerationAttestation from './pageIncludes/signAndReviewAttestations/IncarcerationAttestation';
import IncomeDataUsageAttestation from './pageIncludes/signAndReviewAttestations/IncomeDataUsageAttestation';
import MedicaidContactAttestation from './pageIncludes/signAndReviewAttestations/MedicaidContactAttestation';
import MedicaidCooperationAttestation from './pageIncludes/signAndReviewAttestations/MedicaidCooperationAttestation';
import MoneyPursueAttestation from './pageIncludes/signAndReviewAttestations/MoneyPursueAttestation';
import NumberOfYearsAttestation from './pageIncludes/signAndReviewAttestations/NumberOfYearsAttestation';
import OutsideParentAttestation from './pageIncludes/signAndReviewAttestations/OutsideParentAttestation';
import PageHeader from '../headers/PageHeader';
import PenaltyPerjuryAttestation from './pageIncludes/signAndReviewAttestations/PenaltyPerjuryAttestation';
import ReadyReportChangesAttestation from './pageIncludes/signAndReviewAttestations/ReadyReportChangesAttestation';
import SignatureAttestation from './pageIncludes/signAndReviewAttestations/SignatureAttestation';
import TruthAttestation from './pageIncludes/signAndReviewAttestations/TruthAttestation';
import VoterAndOrganDonorInformation from './pageIncludes/signAndReviewAttestations/VoterAndOrganDonorInformation';

// ActionCreators

import {
    changeData,
    clearMethods,
    setMethods,
    submitApplication
} from '../../../actions/ssap/ssapActionCreators';

import { showModal } from '../../../actions/common/actionCreators';

// Utils

import { preventerTools } from '../../../utils/tools/preventerTools';
import { processJsonTools } from '../../../utils/tools/processJsonTools';
import { processUiProperties } from '../../../utils/tools/processUiProperties';
import { updateDirtyFlags } from '../../../utils/miscellaneous/updateDirtyFlags';

// Constants

import { flowTypes as FLOW_TYPES } from '../../../constants/ssap/flows/flowTypes';
import { INLINE_ELIGIBILITY_VALUES } from '../../../constants/ssap/miscellaneous/miscellaneous';
import { RENEWALS_REJECTION, RENEWALS_YEARS_MAXIMUM } from '../../../constants/ssap/hhmTypes/dropdownOptions';
import states from '../../../constants/configs/states'

// Types

import {
    ApplicationStore,
    Attestations,
    OnChangeArgTypes,
    HouseholdMember,
    ReduxAction,
    SsapSavePayload,
    SsapPage,
    SsapReduxActions
} from '../../../components/ssap/SsapTypes';

export let attestationsConfig;

// Attestations [ ATTESTATION_TYPE ]
//
// {
//     ClaimNoticeAttestation = 'ClaimNoticeAttestation',
//     CoverageEndAttestation = 'CoverageEndAttestation',
//     IncarcerationAttestation = 'IncarcerationAttestation',
//     IncomeDataUsageAttestation = 'IncomeDataUsageAttestation',
//     MedicaidContactAttestation = 'MedicaidContactAttestation',
//     MedicaidCooperationAttestation = 'MedicaidCooperationAttestation',
//     MoneyPursueAttestation = 'MoneyPursueAttestation',
//     NumberOfYearsAttestation = 'NumberOfYearsAttestation',
//     OutsideParentAttestation = 'OutsideParentAttestation',
//     PenaltyPerjuryAttestation = 'PenaltyPerjuryAttestation',
//     ReadyReportChangesAttestation = 'ReadyReportChangesAttestation',
//     SignatureAttestation = 'SignatureAttestation',
//     TruthAttestation = 'TruthAttestation',
//     VoterAndOrganDonorInformation = 'VoterAndOrganDonorInformation'
// }

/**
 *
 * @param {Object} props
 * @returns {React.Component} - Sign and Submit Page
 *
 */
const Page3_17 = (props: SsapPage): ReactElement => {

    const {
        YES
    } = INLINE_ELIGIBILITY_VALUES;

    const {
        data,
        data: {
            ssapApplicationId,
            incarcerationAsAttestedIndicator,
            agreeToUseIncomeData
        },
        dataSnapshot,
        environment: {
            impersonation,
            exchangeName,
            isState,
            stateCode,
            links: {
                PHONE,
                REGISTER_FOR_VOTE
            }
        },
        flow: {
            flowType
        },
        form,
        formName,
        householdMembers,
        methods,
        page,
        structure: {
            bloodRelationshipStructure,
            taxHouseholdStructure
        }
    } = props;

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    interface LocalState {
        signature: string
    }

    const INITIAL_VALUES: LocalState = {
        signature: ''
    };

    const [
        localState,
        setLocalState
    ]: [
        LocalState,
        DispatchFromReact<SetStateAction<LocalState>>
    ] = useState<LocalState>(INITIAL_VALUES);

    const {
        signature
    } = localState;

    const handleSubmit = (): void => {

        const getBody = (): SsapSavePayload => {
            const resolvedData = updateDirtyFlags(data, dataSnapshot);

            return {
                application: JSON.stringify(
                    processUiProperties({
                        ...processJsonTools.convertTaxHousehold(
                            processJsonTools.convertBloodRelationships(
                                resolvedData,
                                bloodRelationshipStructure,
                            ),
                            taxHouseholdStructure as any,
                        ),
                        currentPage: page,
                        finalized: true,
                        maxAchievedPage: page,
                    }),
                ),
                applicationId: ssapApplicationId,
                csrOverride: !!impersonation,
                recalculateIncome: false,
                ssnToUpdate: null,
            };
        };

        methods[formName].onFormSubmit(
            null,
            null,
            (): ReduxAction => props.actions.submitApplication(getBody())
        );
    };

    useEffect(
        (): ReturnType<EffectCallback> => {
            props.actions.setMethods('Page3_17', { handleSubmit });
            return (): void => {
                props.actions.clearMethods('Page3_17');
            };
        },
        [ methods[formName] ]
    );

    const onFieldChange_finConsent = (...args: OnChangeArgTypes): void => {

        const [ newData, , , [ name, value ] ] = args;

        const newState = { ...newData };

        switch(name) {
            case 'agreeToUseIncomeData':
                switch(value) {
                    case true:
                        newState.consentAgreement = true;
                        newState.numberOfYearsAgreed = RENEWALS_YEARS_MAXIMUM;
                        break;
                    case false:
                        newState.consentAgreement = null;
                        newState.numberOfYearsAgreed = null;
                        break;
                }
                break;

            case 'numberOfYearsAgreed':
                switch(value) {
                    case RENEWALS_REJECTION:
                        newState.consentAgreement = false;
                        break;
                    default:
                        newState.consentAgreement = true;
                        break;
                }

        }

        props.actions.changeData(newState);
    };

    const onFieldChange_incarceration = (...args: OnChangeArgTypes): void => {
        let [ newData, indexInArray, , [ name, value ] ] = args;

        if(name === 'incarcerationStatusIndicator' && !value) {
            newData = {
                ...newData,
                incarcerationStatus: {
                    ...newData.incarcerationStatus,
                    incarcerationPendingDispositionIndicator: null
                }
            }
        }

        const newState = { ...props.data };
        newState.taxHousehold.householdMember[indexInArray] = newData;
        props.actions.changeData(newState);
    };

    const onFieldChange_data = (...args: OnChangeArgTypes): void => {
        let [ newData, , , [ name, value ] ] = args;

        if(name === 'incarcerationAsAttestedIndicator' && value) {
            newData = {
                ...newData,
                taxHousehold: {
                    ...newData.taxHousehold,
                    householdMember: newData.taxHousehold.householdMember.map(
                        (hhm: HouseholdMember): HouseholdMember => {
                            return {
                                ...hhm,
                                incarcerationStatus: {
                                    ...hhm.incarcerationStatus,
                                    incarcerationStatusIndicator: false,
                                    incarcerationPendingDispositionIndicator: false
                                }
                            }
                        }
                    )
                }
            }
        }


        props.actions.changeData({
            ...newData
        });
    };

    const onFieldChange_signing = (newData: LocalState): void => {
        setLocalState((state: LocalState): LocalState => ({
            ...state,
            ...newData
        }));
    };

    const onFieldBlur_signing = (): void => {
        setLocalState((state: LocalState): LocalState => ({
            ...state,
            signature: state.signature.trim()
        }));
    };

    const getTopLevelValidation = (): boolean => {
        return incarcerationAsAttestedIndicator
            || householdMembers.some(
                (hhm: HouseholdMember): boolean | null => {
                    return hhm.incarcerationStatus.incarcerationStatusIndicator;
                }
            );
    };

    const isAbsentParent: boolean = useMemo(
        () => householdMembers.some(
            (hhm: HouseholdMember): boolean | null => {
                return hhm.healthCoverage.absentParent;
            }
        ),
        [ householdMembers ]
    );

    const isDisagreeToUseIncomeData: boolean = agreeToUseIncomeData === false;
    const isFinancialFlow: boolean = flowType === FLOW_TYPES.FINANCIAL_FLOW;

    const isEndCoverageAttestationVisible: boolean = useMemo(
        () => {
            const isAnyHhmCitizenOrHasLegalStatus: boolean = householdMembers.some(
                (hhm: HouseholdMember): boolean => {
                    const {
                        citizenshipImmigrationStatus: {
                            citizenshipStatusIndicator,
                            lawfulPresenceIndicator
                        }
                    } = hhm;

                    return !!citizenshipStatusIndicator || !!lawfulPresenceIndicator;
                }
            );

            const isNoOneHhmPreliminaryChipOrMedicaidEligible: boolean = householdMembers.every(
                (hhm: HouseholdMember): boolean => {
                    const {
                        inlineEligibility: {
                            preliminaryChipStatus,
                            preliminaryMedicaidStatus
                        }
                    } = hhm;

                    return preliminaryChipStatus !== YES && preliminaryMedicaidStatus !== YES
                }
            );

            return isFinancialFlow && isAnyHhmCitizenOrHasLegalStatus && isNoOneHhmPreliminaryChipOrMedicaidEligible;
        },
        [ ]
    );

    const getAttestations = (): string | JSX.Element => {

        const defaultProps = {
            [ Attestations.AgreeToFileAttestation ]: {
                actions: {
                    onFieldChange_data
                },
                data,
                formName
            },
            [ Attestations.ClaimNoticeAttestation ]: {
                actions: {
                    onFieldChange_data
                },
                data,
                formName
            },
            [ Attestations.CoverageEndAttestation ]: {
                actions: {
                    onFieldChange_data
                },
                data,
                exchangeName,
                formName
            },
            [ Attestations.IncarcerationAttestation ]: {
                actions: {
                    getTopLevelValidation,
                    onFieldChange_data,
                    onFieldChange_incarceration
                },
                data,
                formName,
                isFormSubmitted
            },
            [ Attestations.IncomeDataUsageAttestation ]: {
                actions: {
                    onFieldChange_finConsent
                },
                data,
                exchangeName,
                formName,
                isFinancialFlow
            },
            [ Attestations.MedicaidContactAttestation ]: {
                actions: {
                    onFieldChange_data
                },
                data,
                formName
            },
            [ Attestations.MedicaidCooperationAttestation ]: {
                actions: {
                    onFieldChange_data
                },
                data,
                formName
            },
            [ Attestations.MoneyPursueAttestation ]: {
                actions: {
                    onFieldChange_data
                },
                data,
                formName
            },
            [ Attestations.NumberOfYearsAttestation ]: {
                actions: {
                    onFieldChange_finConsent
                },
                data,
                exchangeName,
                isFinancialFlow,
                isState,
                formName
            },
            [ Attestations.OutsideParentAttestation ]: {
                actions: {
                    onFieldChange_data
                },
                data,
                formName
            },
            [ Attestations.PenaltyPerjuryAttestation ]: {
                actions: {
                    onFieldChange_data
                },
                data,
                formName
            },
            [ Attestations.ReadyReportChangesAttestation ]: {
                actions: {
                    onFieldChange_data
                },
                data,
                exchangeName,
                formName,
                isFinancialFlow,
                PHONE,
                stateCode
            },
            [ Attestations.SignatureAttestation ]: {
                actions: {
                    onFieldBlur_signing,
                    onFieldChange_signing
                },
                formName,
                householdMembers,
                signature
            },
            [ Attestations.TruthAttestation ]: {
                actions: {
                    onFieldChange_data
                },
                data,
                formName
            },
            [ Attestations.VoterAndOrganDonorInformation ]: {
                isState
            }
        };

        attestationsConfig = {
            [ states.GEORGIA ]: [
                {
                    Attestation: IncarcerationAttestation,
                    attestationName: Attestations.IncarcerationAttestation
                },
                {
                    Attestation: IncomeDataUsageAttestation,
                    attestationName: Attestations.IncomeDataUsageAttestation
                },
                {
                    Attestation: NumberOfYearsAttestation,
                    attestationName: Attestations.NumberOfYearsAttestation,
                    isVisible: isDisagreeToUseIncomeData
                },
                {
                    Attestation: CoverageEndAttestation,
                    attestationName: Attestations.CoverageEndAttestation,
                    isVisible: isEndCoverageAttestationVisible
                },
                {
                    Attestation: MedicaidCooperationAttestation,
                    attestationName: Attestations.MedicaidCooperationAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: MoneyPursueAttestation,
                    attestationName: Attestations.MoneyPursueAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: OutsideParentAttestation,
                    attestationName: Attestations.OutsideParentAttestation,
                    isVisible: isAbsentParent
                },
                {
                    Attestation: ReadyReportChangesAttestation,
                    attestationName: Attestations.ReadyReportChangesAttestation,
                    specificProps: { isFinancialFlow: true }
                },
                {
                    Attestation: TruthAttestation,
                    attestationName: Attestations.TruthAttestation
                },
                {
                    Attestation: SignatureAttestation,
                    attestationName: Attestations.SignatureAttestation
                }
            ],
            [ states.IDAHO ]: [
                {
                    Attestation: IncarcerationAttestation,
                    attestationName: Attestations.IncarcerationAttestation
                },
                {
                    Attestation: IncomeDataUsageAttestation,
                    attestationName: Attestations.IncomeDataUsageAttestation
                },
                {
                    Attestation: NumberOfYearsAttestation,
                    attestationName: Attestations.NumberOfYearsAttestation,
                    isVisible: isDisagreeToUseIncomeData
                },
                {
                    Attestation: CoverageEndAttestation,
                    attestationName: Attestations.CoverageEndAttestation,
                    isVisible: isEndCoverageAttestationVisible
                },
                {
                    Attestation: MedicaidCooperationAttestation,
                    attestationName: Attestations.MedicaidCooperationAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: AgreeToFileAttestation,
                    attestationName: Attestations.AgreeToFileAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: OutsideParentAttestation,
                    attestationName: Attestations.OutsideParentAttestation,
                    isVisible: isAbsentParent
                },
                {
                    Attestation: ReadyReportChangesAttestation,
                    attestationName: Attestations.ReadyReportChangesAttestation,
                    specificProps: { isFinancialFlow: true }
                },
                {
                    Attestation: TruthAttestation,
                    attestationName: Attestations.TruthAttestation
                },
                {
                    Attestation: SignatureAttestation,
                    attestationName: Attestations.SignatureAttestation
                }
            ],
            [ states.ILLINOIS ]: [
                {
                    Attestation: IncarcerationAttestation,
                    attestationName: Attestations.IncarcerationAttestation
                },
                {
                    Attestation: IncomeDataUsageAttestation,
                    attestationName: Attestations.IncomeDataUsageAttestation,
                    isVisible: false
                },
                {
                    Attestation: NumberOfYearsAttestation,
                    attestationName: Attestations.NumberOfYearsAttestation,
                    isVisible: true
                },
                {
                    Attestation: CoverageEndAttestation,
                    attestationName: Attestations.CoverageEndAttestation,
                    isVisible: isEndCoverageAttestationVisible
                },
                {
                    Attestation: OutsideParentAttestation,
                    attestationName: Attestations.OutsideParentAttestation,
                    isVisible: isAbsentParent
                },
                {
                    Attestation: ReadyReportChangesAttestation,
                    attestationName: Attestations.ReadyReportChangesAttestation,
                    specificProps: { isFinancialFlow: true }
                },
                {
                    Attestation: MedicaidCooperationAttestation,
                    attestationName: Attestations.MedicaidCooperationAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: MoneyPursueAttestation,
                    attestationName: Attestations.MoneyPursueAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: VoterAndOrganDonorInformation,
                    attestationName: Attestations.VoterAndOrganDonorInformation,
                    specificProps: { exchangeName }
                },
                {
                    Attestation: TruthAttestation,
                    attestationName: Attestations.TruthAttestation,
                    specificProps: { isFinancialFlow }
                },
                {
                    Attestation: PenaltyPerjuryAttestation,
                    attestationName: Attestations.PenaltyPerjuryAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: SignatureAttestation,
                    attestationName: Attestations.SignatureAttestation
                }
            ],
            [ states.NEW_JERSEY ]: [
                {
                    Attestation: IncarcerationAttestation,
                    attestationName: Attestations.IncarcerationAttestation
                },
                {
                    Attestation: IncomeDataUsageAttestation,
                    attestationName: Attestations.IncomeDataUsageAttestation
                },
                {
                    Attestation: NumberOfYearsAttestation,
                    attestationName: Attestations.NumberOfYearsAttestation,
                    isVisible: isDisagreeToUseIncomeData
                },
                {
                    Attestation: CoverageEndAttestation,
                    attestationName: Attestations.CoverageEndAttestation,
                    isVisible: isEndCoverageAttestationVisible
                },
                {
                    Attestation: MedicaidCooperationAttestation,
                    attestationName: Attestations.MedicaidCooperationAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: OutsideParentAttestation,
                    attestationName: Attestations.OutsideParentAttestation,
                    isVisible: isAbsentParent
                },
                {
                    Attestation: ReadyReportChangesAttestation,
                    attestationName: Attestations.ReadyReportChangesAttestation,
                    specificProps: { isFinancialFlow: true }
                },
                {
                    Attestation: ClaimNoticeAttestation,
                    attestationName: Attestations.ClaimNoticeAttestation
                },
                {
                    Attestation: VoterAndOrganDonorInformation,
                    attestationName: Attestations.VoterAndOrganDonorInformation,
                    specificProps: { REGISTER_FOR_VOTE }
                },
                {
                    Attestation: TruthAttestation,
                    attestationName: Attestations.TruthAttestation
                },
                {
                    Attestation: SignatureAttestation,
                    attestationName: Attestations.SignatureAttestation
                }
            ],
            [ states.NEVADA ]: [
                {
                    Attestation: IncarcerationAttestation,
                    attestationName: Attestations.IncarcerationAttestation
                },
                {
                    Attestation: IncomeDataUsageAttestation,
                    attestationName: Attestations.IncomeDataUsageAttestation
                },
                {
                    Attestation: NumberOfYearsAttestation,
                    attestationName: Attestations.NumberOfYearsAttestation,
                    isVisible: isDisagreeToUseIncomeData
                },
                {
                    Attestation: CoverageEndAttestation,
                    attestationName: Attestations.CoverageEndAttestation,
                    isVisible: isEndCoverageAttestationVisible
                },
                {
                    Attestation: MoneyPursueAttestation,
                    attestationName: Attestations.MoneyPursueAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: OutsideParentAttestation,
                    attestationName: Attestations.OutsideParentAttestation,
                    isVisible: isAbsentParent
                },
                {
                    Attestation: ReadyReportChangesAttestation,
                    attestationName: Attestations.ReadyReportChangesAttestation,
                    specificProps: { isFinancialFlow: true }
                },
                {
                    Attestation: MedicaidContactAttestation,
                    attestationName: Attestations.MedicaidContactAttestation
                },
                {
                    Attestation: TruthAttestation,
                    attestationName: Attestations.TruthAttestation
                },
                {
                    Attestation: SignatureAttestation,
                    attestationName: Attestations.SignatureAttestation
                }
            ],
            [ states.NEW_MEXICO ]: [
                {
                    Attestation: IncarcerationAttestation,
                    attestationName: Attestations.IncarcerationAttestation
                },
                {
                    Attestation: IncomeDataUsageAttestation,
                    attestationName: Attestations.IncomeDataUsageAttestation,
                    isVisible: false
                },
                {
                    Attestation: NumberOfYearsAttestation,
                    attestationName: Attestations.NumberOfYearsAttestation,
                    isVisible: true
                },
                {
                    Attestation: CoverageEndAttestation,
                    attestationName: Attestations.CoverageEndAttestation,
                    isVisible: isEndCoverageAttestationVisible
                },
                {
                    Attestation: OutsideParentAttestation,
                    attestationName: Attestations.OutsideParentAttestation,
                    isVisible: isAbsentParent
                },
                {
                    Attestation: ReadyReportChangesAttestation,
                    attestationName: Attestations.ReadyReportChangesAttestation,
                    specificProps: { isFinancialFlow: true }
                },
                {
                    Attestation: MedicaidCooperationAttestation,
                    attestationName: Attestations.MedicaidCooperationAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: MoneyPursueAttestation,
                    attestationName: Attestations.MoneyPursueAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: VoterAndOrganDonorInformation,
                    attestationName: Attestations.VoterAndOrganDonorInformation,
                    specificProps: { exchangeName }
                },
                {
                    Attestation: TruthAttestation,
                    attestationName: Attestations.TruthAttestation,
                    specificProps: { isFinancialFlow }
                },
                {
                    Attestation: PenaltyPerjuryAttestation,
                    attestationName: Attestations.PenaltyPerjuryAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: SignatureAttestation,
                    attestationName: Attestations.SignatureAttestation
                }
            ],
            [ states.PENNSYLVANIA ]: [
                {
                    Attestation: IncarcerationAttestation,
                    attestationName: Attestations.IncarcerationAttestation
                },
                {
                    Attestation: IncomeDataUsageAttestation,
                    attestationName: Attestations.IncomeDataUsageAttestation,
                    isVisible: false
                },
                {
                    Attestation: NumberOfYearsAttestation,
                    attestationName: Attestations.NumberOfYearsAttestation,
                    isVisible: true
                },
                {
                    Attestation: CoverageEndAttestation,
                    attestationName: Attestations.CoverageEndAttestation,
                    isVisible: isEndCoverageAttestationVisible
                },
                {
                    Attestation: OutsideParentAttestation,
                    attestationName: Attestations.OutsideParentAttestation,
                    isVisible: isAbsentParent
                },
                {
                    Attestation: ReadyReportChangesAttestation,
                    attestationName: Attestations.ReadyReportChangesAttestation,
                    specificProps: { isFinancialFlow: true }
                },
                {
                    Attestation: MedicaidCooperationAttestation,
                    attestationName: Attestations.MedicaidCooperationAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: MoneyPursueAttestation,
                    attestationName: Attestations.MoneyPursueAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: VoterAndOrganDonorInformation,
                    attestationName: Attestations.VoterAndOrganDonorInformation,
                    specificProps: { exchangeName }
                },
                {
                    Attestation: TruthAttestation,
                    attestationName: Attestations.TruthAttestation,
                    specificProps: { isFinancialFlow }
                },
                {
                    Attestation: PenaltyPerjuryAttestation,
                    attestationName: Attestations.PenaltyPerjuryAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: SignatureAttestation,
                    attestationName: Attestations.SignatureAttestation
                }
            ],
            [ states.VIRGINIA ]: [
                {
                    Attestation: IncarcerationAttestation,
                    attestationName: Attestations.IncarcerationAttestation
                },
                {
                    Attestation: IncomeDataUsageAttestation,
                    attestationName: Attestations.IncomeDataUsageAttestation
                },
                {
                    Attestation: NumberOfYearsAttestation,
                    attestationName: Attestations.NumberOfYearsAttestation,
                    isVisible: isDisagreeToUseIncomeData
                },
                {
                    Attestation: CoverageEndAttestation,
                    attestationName: Attestations.CoverageEndAttestation,
                    isVisible: isEndCoverageAttestationVisible
                },
                {
                    Attestation: MedicaidCooperationAttestation,
                    attestationName: Attestations.MedicaidCooperationAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: OutsideParentAttestation,
                    attestationName: Attestations.OutsideParentAttestation,
                    isVisible: isAbsentParent
                },
                {
                    Attestation: ReadyReportChangesAttestation,
                    attestationName: Attestations.ReadyReportChangesAttestation,
                    specificProps: { isFinancialFlow: true }
                },
                {
                    Attestation: PenaltyPerjuryAttestation,
                    attestationName: Attestations.PenaltyPerjuryAttestation,
                    isVisible: isFinancialFlow
                },
                {
                    Attestation: TruthAttestation,
                    attestationName: Attestations.TruthAttestation
                },
                {
                    Attestation: SignatureAttestation,
                    attestationName: Attestations.SignatureAttestation
                }
            ]
        };

        if(!(stateCode in attestationsConfig) || attestationsConfig[stateCode].length === 0) {
            return `There is no attestation config for ${ stateCode }`;
        }

        return attestationsConfig[stateCode].reduce((attestations, attestation, idx) => {
            const {
                Attestation,
                attestationName,
                isVisible = true,
                specificProps = { }
            } = attestation;

            return isVisible
                ? [
                    ...attestations,
                    <span
                        data-testid = { attestationName }
                        key = { idx }
                    >
                        <Attestation
                            { ...{
                                ...defaultProps[attestationName],
                                ...specificProps
                            } }
                        />
                    </span>
                ]
                : attestations;
        }, [ ]);
    };

    return (
        <Fragment>
            <div className = 'fade-in' >
                <div className = 'subsection' >
                    <PageHeader
                        text = 'ssap.subheader.read_and_check_if_agree'
                        headerClassName = 'gi-heading-alt'
                    />
                    <Form
                        { ...{
                            formName,
                            topLevelValidity: getTopLevelValidation()
                        } }
                    >
                        <div className = 'grid-col-12' >
                            { getAttestations() }
                        </div>
                    </Form>
                </div>
            </div>

        </Fragment>
    );
};

const mapStateToProps = (state: ApplicationStore): Partial<SsapPage> => ({

    environment: state.common.environment,

    data: state.ssap.data,
    dataSnapshot: state.ssap.dataSnapshot,
    form: state.ssap.form,
    flow: state.ssap.flow,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    methods: state.ssap.methods,
    page: state.ssap.page,
    structure: state.ssap.structure

});

const mapDispatchToProps = (dispatch: Dispatch): { actions: Partial<SsapReduxActions> } => ({
    actions: bindActionCreators({
        changeData,
        clearMethods,
        setMethods,
        showModal,
        submitApplication
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page3_17);
